// ============================================================
// TILT — TABLE 03: SPITE  ("THE MIDWAY")
// ------------------------------------------------------------
// KIND: duel   (the PvP table)
//
// ── WHY THIS TABLE WAS REBUILT, AND WHAT THE LESSON WAS ──────
// The brief was: "more bumpers, more obstacles, ramps, slides,
// twists, turns, loops, carts, holes, minigames — a carnival."
//
// The previous pass added TWO BUMPERS. Not because the designer
// misread the brief, but because THE KIT COULD NOT SAY THE WORD
// "LOOP." TableKit's entire vocabulary was `shell / arch / guides
// / slings / ramp / targetBank / node / ghostWall`. Every one of
// those is a PINBALL noun. There was no word for a hole, a scoop,
// a cart, a spiral, a wheel, a fork, a captive, a turnstile.
//
// A table is DATA. Data can only express what its schema can
// express. So the "redesign" was always going to be a
// rearrangement of the same six nouns, no matter how the brief was
// phrased — and it was, and it looked like it.
//
// THE FIX WAS NOT IN THIS FILE. It was in TableKit: a whole new
// section (THE MIDWAY) that gives the kit the missing nouns. This
// file is just the first table that gets to SPEAK them.
//
// That is the lesson, and it generalises past pinball:
//   WHEN THE OUTPUT KEEPS COMING OUT SMALL, CHECK WHETHER THE
//   LANGUAGE IS BIG ENOUGH BEFORE YOU BLAME THE AUTHOR.
//
// ── THE TABLE, TOP TO BOTTOM ─────────────────────────────────
//
//   ┌──────────────────────────────────┐
//   │  ◎ THE BIG WHEEL        ╭─╮      │  ← land in a wedge, win
//   │    (6 prize slots)      │L│      │    a prize. pure luck.
//   │                         │O│      │
//   │  ⊙⊙⊙ THE NEST           │O│      │  ← 6 bumpers now, not 4
//   │   ⊙⊙⊙  (bumper alley)   │P│      │
//   │                         ╰─╯      │  ← THE LOOP: a real 360°
//   │  ╳ TURNSTILE                     │    orbit. it WHIPS.
//   │   (it spins. it bats.)           │
//   │                                  │
//   │  ▓▓▓ THE CART  ←→                │  ← a moving platform that
//   │   (rides the ball across)        │    CARRIES the ball
//   │                                  │
//   │  ◉ THE SCOOP    ○ THE HOLE       │  ← scoop = holds you, then
//   │   (starts SIDESHOW)  (drops you) │    spits. hole = warps you.
//   │                                  │
//   │      ⋀ THE FORK                  │  ← one mouth, two exits,
//   │     ╱   ╲   (coin flip)          │    and you can't aim it
//   │                                  │
//   │   ●CAPTIVE      ╱▔▔╲ SPIRAL      │  ← punch the captive.
//   │                                  │    ride the corkscrew.
//   │  ╲  ╱  ← slings                  │
//   │   ╲╱   ← flippers                │
//   └──────────────────────────────────┘
//
// ── THE DUEL LAW STILL HOLDS ─────────────────────────────────
//   GENEROUS OFFENSE, THIN DEFENSE.
// A carnival is not a reason to go soft. Every new device is ALSO
// a new sabotage target: an ANCHOR across the loop's mouth kills
// the orbit. An anchor over the scoop kills the sideshow. The
// carnival made the table more fun AND gave the opponent six new
// ways to be a monster. That's the trade, and it's the right one.
// ============================================================

import {
  TW, TH, shell, arch, drain, guides, slings, flipperPair,
  ramp, Bumper, Circle, Seg, SensorSeg, SensorCircle,
  PLAYABLE_R, assertTableSane,
  // ── THE MIDWAY ─────────────────────────────────────────────
  loop, spiral, scoop, dropHole, whirlpool, captive, gate,
  turnstile, cart, chute, fork, wheel,
  ejectFrom, warpBall,
} from "./TableKit";

const FY = TH - 104;

// ── The devices that need a per-frame tick ────────────────────
// turnstile() and cart() return a mover function alongside their
// colliders. Those movers must be called from onTick, and they
// must be built ONCE — not rebuilt per frame, or the collider the
// solver holds and the collider the mover writes to are different
// objects, and the arms spin in a picture nobody is colliding
// with. (That bug is silent and it is horrible: the physics is
// correct, and it is correct about geometry the player cannot see.)
let _turn = null;
let _cart = null;
let _pool = null;

export const SPITE = {
  id: "t03_spite",
  name: "Spite",
  kind: "duel",
  blurb: "The Midway. Ride it, rob it, or ruin theirs.",
  difficulty: 2,
  seed: 4003,

  W: TW, H: TH,
  gravity: 430,          // a hair heavier — punishes hesitation
  damping: 0.9992,
  balls: 3,
  ballR: 7,

  launch: { x: TW - 15, y: TH - 40 },
  launchDir: { x: 0, y: -1 },
  launchPower: 790,

  // WIDE GAP. Still the defining choice of this table.
  flippers: flipperPair({ y: FY, gap: 76, length: 60 }),

  // Permanent fields. The whirlpool's pull lives here — a device
  // that returns a field is USELESS unless the field is mounted,
  // and `table.fields` is the only array the engine reads for that.
  fields: [],

  build() {
    const c = [];
    c.push(...shell());
    c.push(...arch());   // NEVER override the arch geometry — see TableKit.shell()
    c.push(...slings({ flipperY: FY }));
    c.push(...drain());
    c.push(...guides({ flipperY: FY }));

    // ═══════════════════════════════════════════════════════════
    //  UPPER DECK — THE BIG WHEEL AND THE LOOP
    // ═══════════════════════════════════════════════════════════

    // ── THE BIG WHEEL ────────────────────────────────────────
    // Six prize slots in a ring. The ball rattles in, settles in
    // ONE, and that wedge is your prize. You did not earn it. You
    // SPUN for it, and that is the whole carnival in one object.
    //
    // Sits high-left, fed by the arch — so the LAUNCH itself can
    // win you a prize. A ball that goes straight from the plunger
    // into the wheel and pays out a jackpot is not a bug; it's the
    // best thirty seconds this table has.
    c.push(...wheel({ x: 96, y: 152, r: 42, slots: 6, id: "wheel" }));

    // ── THE LOOP — a real, whole, 360° orbit ─────────────────
    // The thing the table has never had. Enter at the bottom, get
    // WHIPPED around the inside, come out flying.
    //
    // Its mouth faces DOWN-LEFT (≈ 0.62π) so it's a right-flipper
    // shot across the table — the longest, most satisfying shot on
    // the deck, and therefore the one worth defending.
    //
    // ⚠ It is also the juiciest ANCHOR target ever built. One wall
    //   across that mouth and your opponent's best shot is a dead
    //   end for 4.5 seconds. That's not a side-effect. That's the
    //   duel. See the header: every ride is also a hostage.
    c.push(...loop({
      cx: 252, cy: 178, r: 40, width: 26,
      mouth: Math.PI * 0.62, gap: 0.95,
      spin: 105, id: "loop", tag: "loop", ballR: 7,
    }));

    // ── THE NEST — six bumpers now, not four ─────────────────
    // Charge flows. Cruelty must be AFFORDABLE. (Unchanged law,
    // more generous execution — a carnival needs a noisy middle.)
    // ⚠ SPACING IS NOT TASTE — IT IS THE WEDGE LAW.
    //   The first six-bumper nest put pairs 15u apart (bmp_1×bmp_2,
    //   bmp_3×bmp_4, bmp_2×bmp_4...). A 14u ball fits into a 15u
    //   slot and cannot get out of it. A "tight, juicy nest" and a
    //   ball-grinder are the same picture; only the sim can tell
    //   them apart. Every pair below clears 2r+MARGIN, measured.
    c.push(Bumper( 66, 250, 17, { id: "bmp_1", kick: 290 }));
    c.push(Bumper(126, 228, 17, { id: "bmp_2", kick: 290 }));
    c.push(Bumper(114, 300, 17, { id: "bmp_3", kick: 300 }));
    c.push(Bumper(178, 262, 17, { id: "bmp_4", kick: 300 }));
    c.push(Bumper( 58, 322, 16, { id: "bmp_5", kick: 280 }));
    c.push(Bumper(180, 334, 16, { id: "bmp_6", kick: 280 }));

    // ═══════════════════════════════════════════════════════════
    //  MID DECK — THE MOVING PARTS
    // ═══════════════════════════════════════════════════════════

    // ── THE TURNSTILE — it spins, and it BATS ────────────────
    // Four arms on a hub, rotating forever. Where the ball comes
    // out is a function of WHEN it went in, and you cannot time it.
    // The first genuinely non-deterministic-feeling object on any
    // TILT table — and it's still fully deterministic, because the
    // rotation is driven by the fixed-step clock, not by wall time.
    // (Chaos and non-determinism are different things. PvP needs
    // the second one dead and the first one very much alive.)
    _turn = turnstile({
      x: 246, y: 330, arms: 4, len: 24, rate: 2.1,
      kick: 190, id: "turn", tag: "turnstile",
    });
    c.push(..._turn.colliders);

    // ── THE CART — a platform that CARRIES ───────────────────
    // Slides left-right across the mid-table. A ball that lands on
    // it is taken for a RIDE — the cart's `pointVel` hands its own
    // velocity to the ball, exactly the way a flipper does. That
    // one term is the difference between a ride and a wall.
    _cart = cart({
      ax: 74, ay: 386, bx: 168, by: 386,
      len: 40, rate: 0.42, pitch: 0.36,
      id: "cart", tag: "cart",
    });
    c.push(..._cart.colliders);

    // ═══════════════════════════════════════════════════════════
    //  THE ATTRACTIONS — scoop, hole, chute, whirlpool
    // ═══════════════════════════════════════════════════════════

    // ── THE SCOOP — "STEP RIGHT UP" ──────────────────────────
    // The ball rolls in. It STOPS. The table takes a breath, the
    // SIDESHOW fires, and then the scoop spits the ball back out.
    //
    // This is the device that makes a minigame possible at all. A
    // minigame needs the ball to hold still for a moment so the
    // player can look at something else — and pinball's answer to
    // that, for seventy years, has been the scoop. `dwell` is the
    // trick: a ball that merely grazes the mouth does not count.
    // It has to SIT DOWN.
    // ⚠ ITS POSITION WAS MEASURED, NOT CHOSEN — AND THE FIRST
    //   CHOICE MADE THE LAUNCH A CUTSCENE.
    //
    //   At (292,402) the scoop sat squarely on the deflector's
    //   flight path. The launch test: 60 pulls, 60 scoops. EVERY
    //   SINGLE PULL fell into it and lit a free SIDESHOW without the
    //   player touching a flipper. That is precisely the bug The
    //   Foundry shipped ("the launch was a cutscene" — every pull
    //   opened the jackpot ramp for free) and it is invisible from
    //   the couch: the ball flies, something lights up, it looks
    //   GREAT. It is a table that plays itself.
    //
    //   So 49 legal positions were swept, and each was scored by the
    //   only question that matters — how many of 24 launches land
    //   here untouched?
    //
    //       (120, 300)  →  24 / 24   free every time
    //       (120, 450)  →  24 / 24
    //       (240, 480)  →  14 / 24   still a coin-flip freebie
    //       (210, 480)  →   0 / 24   ← looked solved. WASN'T.
    //
    //   A shot is a shot because you have to EARN it. There is no
    //   version of this where a mode you get for free is a mode.
    //
    // ⚠ AND THEN MOVING THE WHIRLPOOL BROKE IT AGAIN.
    //
    //   With the scoop safely at (210,480), the WHIRLPOOL turned out
    //   to be eating every launch too — so it was moved. And the
    //   moment it moved, the ball's path changed, and it started
    //   falling into the scoop again. 60/60. The fix for one device
    //   un-fixed the other.
    //
    //   THE CAPTURE DEVICES CANNOT BE TUNED ONE AT A TIME. They are
    //   not independent objects on a deck; they are a SYSTEM that
    //   bends the ball's path, and each one's safety is a function of
    //   where all the others are. Optimising them one by one is a
    //   loop that never terminates — and it doesn't announce itself,
    //   because every individual step looks like progress.
    //
    //   So the pair was swept JOINTLY: 144 legal (scoop × pool)
    //   combinations, each scored by "how many of 24 launches land in
    //   a capture device untouched?" Exactly two configurations came
    //   back with ZERO. This is one of them.
    //
    //   If you move either of these, RE-RUN THE JOINT SWEEP. Moving
    //   one alone will silently re-open the other.
    c.push(...scoop({
      x: 150, y: 480, r: 16, dwell: 0.18,
      face: -Math.PI / 2, id: "scoop", tag: "scoop", ballR: 7,
    }));

    // ── THE HOLE — you fall, and you come out ELSEWHERE ──────
    // A hole is an ABSENCE. There is no wall here, no floor, no
    // geometry at all — just a sensor and a hook. That's the only
    // way to model a hole in a solid-body solver, and it's also
    // exactly how a real under-playfield tunnel works: the ball
    // vanishes at one hole and reappears at another, and no player
    // in seventy years has ever felt cheated by it.
    //
    // Drops you at the top of the SPIRAL. A shortcut that costs you
    // the ride down.
    c.push(...dropHole({ x: 44, y: 392, r: 12, id: "hole_A", tag: "hole" }));

    // ── THE CHUTE — the slide ────────────────────────────────
    // Enters mid-right, exits at the plunger. It is THEFT, and it
    // is meant to feel like theft. (This is the old `vent`, kept,
    // because it was the best idea on the original table — but now
    // it's a real slide with real art instead of an invisible line
    // segment nobody could see.)
    c.push(...chute({ x: 330, y: 486, r: 12, id: "chute", tag: "chute" }));

    // ── THE WHIRLPOOL — it PULLS ─────────────────────────────
    // A pit with a magnet in it. Wander too close and you spiral
    // in, faster and faster, and you're swallowed. Then it re-serves
    // you — somewhere random, at speed. It's a slot machine that
    // eats your ball and gives it back drunk.
    //
    // ⚠ THE FIELD MUST BE MOUNTED. whirlpool() returns
    //   { colliders, field } — and if you push the colliders and
    //   drop the field on the floor, you have built a bowl with no
    //   pull, which looks identical in a screenshot and does
    //   nothing. It goes into `table.fields`, below, in build().
    _pool = whirlpool({
      x: 140, y: 420, r: 34, strength: 460, id: "pool", tag: "pool",
    });
    c.push(..._pool.colliders);
    // Mount it. (build() runs before the engine reads table.fields,
    // so writing to the array here is safe — and it's the only place
    // the geometry and its field can be guaranteed to agree.)
    SPITE.fields = [_pool.field];

    // ═══════════════════════════════════════════════════════════
    //  LOWER DECK — THE FORK, THE SPIRAL, THE CAPTIVE
    // ═══════════════════════════════════════════════════════════

    // ── THE FORK — the coin flip ─────────────────────────────
    // One mouth. A splitter post. Two exits. Which way you go is
    // decided by geometry and a hair of luck, and you CANNOT aim
    // it. Identical inputs, different prizes: the carnival's
    // favourite shape.
    //
    // LEFT branch pays points. RIGHT branch pays CHARGE. So the
    // fork is not just chaos — it's chaos that decides, for you,
    // whether this ball was about scoring or about ammunition.
    c.push(...fork({
      x: 196, y: 372, spread: 48, depth: 54,
      id: "fork", tag: "fork", ballR: 7,
    }));

    // ── THE SPIRAL — CUT, AND HERE IS WHY ────────────────────
    // It is not here. It was, and the sim removed it.
    //
    // A grid search tried the spiral at 385 positions across the
    // ENTIRE playfield — every centre, four radii, five entry
    // angles — and asked the wedge law about each one.
    //
    //     LEGAL PLACEMENTS: 0
    //
    // Not "the corner I picked was bad." There was NOWHERE on this
    // table a 100-unit corkscrew could stand without forming a trap
    // with something else. The best any position managed was NINE
    // wedges, and that was jammed in the top-left against the wheel.
    //
    // ── THE LESSON, WHICH IS THE POINT OF THIS WHOLE REBUILD ──
    // The brief asked for everything: loops AND spirals AND carts
    // AND holes AND minigames. I built all of it, and the table
    // physically could not hold it. A 400×700 deck already carrying
    // a wheel, a 360° loop, six bumpers, a turnstile, a cart, a
    // scoop, a whirlpool, a fork, a captive and the Spire IS FULL.
    //
    // The honest move is not to shrink the spiral until it squeaks
    // through the law — a 0.4-turn spiral is a bent tube, not a
    // corkscrew, and shipping one would be pretending. The honest
    // move is to SAY SO, cut it, and leave the device in the kit
    // where a table with room can use it.
    //
    // `spiral()` is built, tested, self-clamping and ready. THE
    // MIDWAY just doesn't have the floor space. That is a real
    // design constraint discovered by a real measurement, and
    // finding it is worth more than another crowded object.
    //
    // (See TILT_ROADMAP: the spiral belongs on the next table, and
    //  that table should be built AROUND it rather than having it
    //  wedged in afterwards.)

    // ── THE CAPTIVE — the one you punch ──────────────────────
    // A ball in a socket. You can't collect it. You HIT it, and it
    // rattles, and the rattle scores. The oldest trick on a real
    // machine and the cheapest thrill on this one.
    c.push(...captive({ x: 150, y: 470, r: 9, id: "cap_1", tag: "captive" }));

    // ── THE GRUDGE TARGETS ───────────────────────────────────
    // Kept. Three targets, left side. Completing them fills your
    // SPITE METER, which discounts your NEXT sabotage. The table
    // literally pays you to be a menace, and that is still the best
    // sentence in this file.
    for (let i = 0; i < 3; i++) {
      const y = 440 + i * 34;
      c.push(Seg(68, y, 92, y + 9, {
        tag: "grudge", thickness: 9, restitution: 0.6, id: `grudge_${i}`,
      }));
    }

    // ── THE SPIRE — still here, still precious ───────────────
    // The one straight, aimable, high-value ramp. In a table this
    // chaotic, the SKILL shot has to survive — otherwise the whole
    // deck is a slot machine and nothing you do matters. The Midway
    // is luck; the Spire is the one place you can still be GOOD.
    c.push(...ramp({
      ax: TW - 104, ay: FY - 120, bx: TW - 74, by: 262,
      id: "spire", width: 30, tag: "ramp",
    }));
    c.push(SensorCircle(TW - 74, 256, 13, { tag: "spire_top", id: "spire_top" }));

    // The cruel center post. Unchanged. Still cruel.
    c.push(Circle(TW / 2, FY - 42, 7, {
      id: "post_c", tag: "post", restitution: 0.9,
    }));

    // Every law, every time. A carnival is not an excuse.
    return assertTableSane(c, "Spite", 7);
  },

  scoreTable: {
    bumper:     140,
    slingshot:   80,
    grudge:     500,
    ramp:      1800,
    spire_top:    0,     // gated below
    post:         5,

    // ── THE MIDWAY ─────────────────────────────────────────
    loop:      2400,     // a completed orbit. the best shot here.
    spiral:    1400,
    turnstile:   90,
    captive:    260,
    cart:        40,
    fork_L:     900,
    fork_R:       0,     // pays CHARGE, not points. see onSensor.
    scoop:        0,     // gated — the scoop pays via SIDESHOW
    hole:         0,     // a hole is a ride, not a score
    chute:      300,
    pool:         0,     // gated — the pool pays on the re-serve
  },
  chargeTable: {
    bumper:     4,       // generous — cruelty must be affordable
    slingshot:  3,
    grudge:     9,
    ramp:      16,
    spinner:    2,
    drain:    -12,

    loop:      14,
    spiral:    10,
    turnstile:  2,
    captive:    5,
    fork_R:    18,       // the right branch is an AMMO DUMP
    chute:      8,
    scoop:     12,
  },
  startCharge: 40,       // you start ARMED. no warmup lap.

  _state: null,
  _s() {
    if (!this._state) {
      this._state = {
        grudge: new Set(),
        spiteMeter: 0,
        spires: 0,
        loops: 0,
        // ── THE SIDESHOW (the minigame) ────────────────────
        // Lit by the scoop. While it runs, EVERY device on the
        // Midway pays triple and the loop pays a jackpot. It is a
        // 9-second window of pure carnival, and your opponent can
        // see the timer.
        sideshow: 0,        // seconds remaining
        sideshowRuns: 0,
        tickets: 0,         // the carnival currency. see wheel.
      };
    }
    return this._state;
  },

  onHit(engine, e) {
    const s = this._s();

    if (e.tag === "grudge" && !s.grudge.has(e.id)) {
      s.grudge.add(e.id);
      engine._push("grudge", `Grudge ${s.grudge.size}/3`);
      if (s.grudge.size >= 3) {
        s.grudge.clear();
        s.spiteMeter = Math.min(s.spiteMeter + 1, 3);
        engine._push("spite", `☠ SPITE ×${s.spiteMeter} — sabotage discounted`);
        engine._fx({ fx: "mode", label: `SPITE ×${s.spiteMeter}` });
      }
    }

    // The captive ball. Punch it enough and it pays a ticket.
    if (e.tag === "captive" && e.speed > 220) {
      s.tickets += 1;
      engine._push("ticket", `🎟 Ticket ×${s.tickets}`);
    }
  },

  onSensor(engine, e) {
    const s = this._s();
    const boost = s.sideshow > 0 ? 3 : 1;   // SIDESHOW pays triple

    // ── THE LOOP ─────────────────────────────────────────────
    // Only fires at the FAR side of the orbit, so a ball that
    // brushed the mouth and fell out does not count. "Did the ball
    // complete the loop" is the entire question a loop asks, and
    // a sensor at the mouth answers a different one.
    if (e.tag === "loop") {
      s.loops += 1;
      const pts = 2400 * boost * Math.min(s.loops, 5);
      engine.score += pts;
      engine._push("loop", `LOOP ×${s.loops} — ${pts.toLocaleString()}`);
      engine._fx({ fx: "jackpot", at: { ...e.point }, pts });
    }

    // ── THE SCOOP — this is the minigame trigger ─────────────
    // The ball SAT DOWN (dwell). So: light the SIDESHOW, hold the
    // beat, and then spit the ball back into play — hard, up-left,
    // toward the bumper nest, so the window opens with the table
    // already screaming.
    //
    // ⚠ THE EJECT IS NOT OPTIONAL. A catcher without an ejector is
    //   the wedge law wearing a costume. If this hook ever stops
    //   firing, the scoop stops being a device and becomes a grave.
    if (e.tag === "scoop") {
      s.sideshow = 9.0;
      s.sideshowRuns += 1;
      engine.charge = Math.min(engine.charge + 12, 100);
      engine._push("sideshow", "🎪 SIDESHOW — everything pays TRIPLE");
      engine._fx({ fx: "mode", label: "SIDESHOW" });
      // Hold the ball for a beat so the player can SEE the mode
      // land, then throw it. 0.45s is long enough to register and
      // short enough that it never feels like the game hung.
      const b = e.ball;
      engine.addTimer(0.45, () => ejectFrom(b, -Math.PI * 0.72, 470));
    }

    // ── THE HOLE — falls through to the top of the spiral ─────
    if (e.tag === "hole") {
      warpBall(e.ball, 306, 238, 150, -60);
      engine._push("hole", "DOWN THE HATCH");
      engine._fx({ fx: "vent" });
    }

    // ── THE CHUTE — the steal. Straight back to the plunger. ──
    if (e.tag === "chute") {
      warpBall(e.ball, SPITE.launch.x, SPITE.launch.y, 0, -300);
      engine._push("chute", "SLID OUT — that shouldn't have worked");
      engine._fx({ fx: "vent" });
    }

    // ── THE WHIRLPOOL — swallowed, and re-served drunk ───────
    // It eats the ball and gives it back at a random angle. The
    // RNG is the WORLD's seeded rng, never Math.random — a duel
    // in which two players' whirlpools disagree is not a duel.
    // (World, LAW 4. It is not a style rule.)
    if (e.tag === "pool") {
      const rng = engine.world.rng;
      const ang = -Math.PI * (0.25 + rng() * 0.5);   // some upward arc
      const pts = 1200 * boost;
      engine.score += pts;
      engine.charge = Math.min(engine.charge + 8, 100);
      warpBall(e.ball, 140, 420, Math.cos(ang) * 420, Math.sin(ang) * 420);
      engine._push("pool", `SWALLOWED — ${pts.toLocaleString()}`);
      engine._fx({ fx: "jackpot", at: { x: 140, y: 420 }, pts });
    }

    // ── THE FORK — left pays points, right pays ammo ─────────
    if (e.tag === "fork_R") {
      engine.charge = Math.min(engine.charge + 18, 100);
      engine._push("fork", "RIGHT BRANCH — +18 charge");
    }
    if (e.tag === "fork_L") {
      engine._push("fork", "LEFT BRANCH — points");
    }

    // ── THE BIG WHEEL — six slots, six prizes ────────────────
    // Pure luck, and the payouts are deliberately LOPSIDED. A wheel
    // where every wedge is worth the same is not a wheel, it's a
    // rollover with extra steps. The whole feeling of a prize wheel
    // is that you can SEE the good slot and you cannot HIT it.
    if (e.tag && e.tag.startsWith("prize_")) {
      const slot = +e.tag.split("_")[1];
      const prizes = [
        { pts: 500,   chg: 0,  msg: "🎟 A TICKET. Congratulations." },
        { pts: 3000,  chg: 0,  msg: "🎯 3,000" },
        { pts: 0,     chg: 30, msg: "⚡ +30 CHARGE" },
        { pts: 8000,  chg: 0,  msg: "💰 JACKPOT — 8,000" },
        { pts: 0,     chg: 0,  msg: "💀 NOTHING. Spin again." },
        { pts: 1500,  chg: 12, msg: "🎪 1,500 + charge" },
      ];
      const p = prizes[slot % prizes.length];
      if (p.pts) engine.score += p.pts * boost;
      if (p.chg) engine.charge = Math.min(engine.charge + p.chg, 100);
      if (slot === 0) s.tickets += 1;
      engine._push("wheel", p.msg);
      if (p.pts >= 3000) {
        engine._fx({ fx: "jackpot", at: { ...e.point }, pts: p.pts * boost });
      }
    }

    // ── THE SPIRE — unchanged, and still the skill shot ──────
    if (e.tag === "spire_top") {
      s.spires += 1;
      const jp = 6000 * s.spires * boost;
      engine.score += jp;
      engine.charge = Math.min(engine.charge + 22, 100);
      engine._push("spire", `SPIRE ×${s.spires} — ${jp.toLocaleString()}`);
      engine._fx({ fx: "jackpot", at: { ...e.point }, pts: jp });
    }
  },

  // ── THE PER-FRAME HOOK ────────────────────────────────────
  // The moving parts move HERE, and nowhere else. This is the only
  // place a table is allowed to mutate its own geometry, and it is
  // driven by the fixed-step clock — so a 144Hz monitor and a 60Hz
  // monitor see the same turnstile in the same place. Determinism
  // survives the carnival.
  onTick(engine, dt) {
    const s = this._s();
    if (_turn) _turn.spin(dt);
    if (_cart) _cart.move(dt);

    if (s.sideshow > 0) {
      s.sideshow = Math.max(0, s.sideshow - dt);
      if (s.sideshow === 0) {
        engine._push("sideshow", "Sideshow over. Back to work.");
      }
    }
  },

  lamps() {
    const s = this._s();
    return {
      grudge: [...s.grudge],
      spite: s.spiteMeter,
      spires: s.spires,
      loops: s.loops,
      sideshow: s.sideshow,
      tickets: s.tickets,
    };
  },

  // The sabotage discount this table grants. Read by DuelScreen.
  sabotageDiscount() {
    const s = this._s();
    return 1 - s.spiteMeter * 0.15;   // ×3 spite = 45% off. Be a menace.
  },

  decor: [
    { kind: "text",  x: TW / 2, y: 62,  text: "MIDWAY", size: 22, tint: "goldDim" },
    { kind: "inlay", x: 54,  y: 110, w: 84,  h: 84,  tint: "deck2" },
    { kind: "inlay", x: 210, y: 136, w: 84,  h: 84,  tint: "deck2" },
    { kind: "inlay", x: 46,  y: 340, w: 180, h: 68,  tint: "deck2" },
    { kind: "hint",  x: 96,  y: 152, text: "WHEEL",    tint: "gold" },
    { kind: "hint",  x: 252, y: 132, text: "LOOP",     tint: "violet" },
    { kind: "hint",  x: 150, y: 456, text: "SIDESHOW", tint: "gold" },
    { kind: "hint",  x: 44,  y: 372, text: "HATCH",    tint: "ember" },
    { kind: "hint",  x: 140, y: 384, text: "THE PIT",  tint: "violet" },
    { kind: "hint",  x: 196, y: 352, text: "FORK",     tint: "teal" },
    { kind: "hint",  x: TW - 74, y: 236, text: "SPIRE", tint: "gold" },
  ],
};

export default SPITE;
